/* Client-side image processing, run on EVERY image before upload so nothing raw
   from a phone ever reaches R2/the CDN. It:
   - downscales to a sane max dimension (Core Web Vitals + storage),
   - bakes in EXIF orientation (no more sideways photos),
   - re-encodes to WebP (JPEG fallback), which also STRIPS all EXIF metadata,
     including the GPS coordinates a phone embeds (privacy).
   If a file can't be decoded in this browser (e.g. HEIC on desktop Chrome) it's
   returned unchanged so the upload still succeeds. */

let webpOk: boolean | null = null;
function supportsWebp(): boolean {
  if (webpOk !== null) return webpOk;
  try {
    const c = document.createElement('canvas');
    c.width = 1; c.height = 1;
    webpOk = c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch { webpOk = false; }
  return webpOk;
}

/** Processed file plus the dimensions we already had to compute to resize it.
    Zero when the browser could not decode the image and it passed through raw. */
export interface SizedImage { file: File; width: number; height: number }

/* The gallery model stores width/height per asset because a client's gallery
   renders ON DEMAND: astro:assets learns a remote image's size by downloading
   it, which is fine once per build and unusable once per request. The canvas
   pass below already knows both numbers, so collecting them costs nothing. */
/* `mime` forces the output format. WebP is the right default for the web, but
   DNN's EasyDNNnews downloads an article's image and re-processes it server-side
   with .NET imaging, which does not read WebP — a WebP hero pushed to DNN simply
   never appears. Callers delivering to DNN ask for JPEG. */
export interface ProcessOpts { maxDim?: number; quality?: number; mime?: 'image/webp' | 'image/jpeg' }

export async function processImageSized(file: File, opts?: ProcessOpts): Promise<SizedImage> {
  const out = await processImageInner(file, opts);
  return out;
}

export async function processImage(file: File, opts?: ProcessOpts): Promise<File> {
  return (await processImageInner(file, opts)).file;
}

async function processImageInner(file: File, opts?: ProcessOpts): Promise<SizedImage> {
  const maxDim = opts?.maxDim ?? 2000;
  const quality = opts?.quality ?? 0.82;
  const looksImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i.test(file.name);
  const raw: SizedImage = { file, width: 0, height: 0 };
  if (!looksImage) return raw;

  let bitmap: ImageBitmap;
  try {
    // `from-image` applies the EXIF orientation while decoding.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return raw; // can't decode here — let the raw file upload, dimensions unknown
  }

  try {
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return raw;
    ctx.drawImage(bitmap, 0, 0, w, h);

    const type = opts?.mime ?? (supportsWebp() ? 'image/webp' : 'image/jpeg');
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
    if (!blob) return raw;

    const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
    const ext = type === 'image/webp' ? 'webp' : 'jpg';
    return { file: new File([blob], `${base}.${ext}`, { type }), width: w, height: h };
  } finally {
    bitmap.close?.();
  }
}

/* Turn a stored photo by quarter turns, keeping its format.
 *
 * Decoded with `from-image` for the same reason uploads are: the turn the user
 * chose is relative to the photo as they SAW it, which is with any EXIF
 * orientation applied. The output carries no EXIF, so it can never be turned a
 * second time by a viewer that reads the tag when the one in tritonIQ did not.
 *
 * Not downscaled — this is the same photo, not a new upload. Quality is set
 * high because a photo turned four times has been re-encoded four times. Throws
 * when the browser cannot write the format (Safari and WebP): the server refuses
 * a format change anyway, and saying so here gives the better message. */
export async function rotateImage(blob: Blob, quarterTurns: number, name = 'photo'): Promise<SizedImage> {
  const turns = ((quarterTurns % 4) + 4) % 4;
  const type = blob.type;
  const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  try {
    const sideways = turns % 2 === 1;
    const w = sideways ? bitmap.height : bitmap.width;
    const h = sideways ? bitmap.width : bitmap.height;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot edit images.');
    ctx.translate(w / 2, h / 2);
    ctx.rotate((turns * Math.PI) / 2);
    ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

    const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.92));
    if (!out || out.type !== type) {
      throw new Error(`This browser cannot save ${type.split('/')[1]?.toUpperCase() || 'this'} images. Try Chrome or Edge.`);
    }
    return { file: new File([out], name, { type }), width: w, height: h };
  } finally {
    bitmap.close?.();
  }
}
